import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { useCandidatoContext } from "./CandidatoContext";
import { listarMensagens } from "../api/mensagens";

interface Usuario {
  id: number;
  nome: string;
  role: string;
}

export interface Mensagem {
  id: number;
  tipo: string;
  conteudo: string;
  criado_em: string;
  resposta_de_id: number | null;
  remetente: Usuario;
}

export interface Conversa {
  raiz: Mensagem;
  respostas: Mensagem[];
  ultima: Mensagem;
}

interface MensagensContextValue {
  lista: Mensagem[];
  conversas: Conversa[];
  aoVivo: boolean;
  naoLida: (raizId: number, ultimaCriadoEm: string) => boolean;
  marcarVista: (raizId: number, criadoEm: string) => void;
  contagemNaoLidas: number;
  recarregar: () => Promise<void>;
}

const MensagensContext = createContext<MensagensContextValue | null>(null);

// Mesmas regras de acesso do item "Mensagens" na sidebar (admin não participa).
const PAPEIS_COM_ACESSO = ["candidato", "lideranca", "assessor"];

const CHAVE_VISTO = "mensagens_vistoEm";

function carregarVistoSalvo(): Record<number, string> {
  try {
    const bruto = localStorage.getItem(CHAVE_VISTO);
    return bruto ? JSON.parse(bruto) : {};
  } catch {
    return {};
  }
}

function salvarVisto(mapa: Record<number, string>) {
  try {
    localStorage.setItem(CHAVE_VISTO, JSON.stringify(mapa));
  } catch {
    // localStorage indisponível (ex: modo privado) — segue sem persistir.
  }
}

export function MensagensProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { selecionado } = useCandidatoContext();

  // Se admin selecionou candidato, usa role "candidato"
  const roleEfetivo = user?.role === "admin" && selecionado ? "candidato" : user?.role;
  const temAcesso = !!user && PAPEIS_COM_ACESSO.includes(roleEfetivo ?? "");

  const [lista, setLista] = useState<Mensagem[]>([]);
  const [aoVivo, setAoVivo] = useState(false);
  const [vistoEm, setVistoEm] = useState<Record<number, string>>(() => carregarVistoSalvo());
  const inicializadoRef = useRef(false);

  // Marca como "vista" só o que já existia no primeiro carregamento — conversas
  // novas (criadas depois, via SSE) não passam por aqui e ficam corretamente
  // não-lidas até serem abertas.
  const recarregar = async () => {
    const roleEfetivo = user?.role === "admin" && selecionado ? "candidato" : user?.role;
    const temAcessoLocal = !!user && PAPEIS_COM_ACESSO.includes(roleEfetivo ?? "");
    if (!temAcessoLocal) return;
    const candidatoId = user?.role === "admin" ? selecionado || undefined : undefined;
    const dados: Mensagem[] = await listarMensagens(candidatoId ? { candidato_id: candidatoId } : {});
    setLista(dados);
    if (inicializadoRef.current) return;
    inicializadoRef.current = true;
    setVistoEm((prev) => {
      const seed = { ...prev };
      dados
        .filter((m) => !m.resposta_de_id)
        .forEach((raiz) => {
          if (seed[raiz.id] !== undefined) return;
          const respostas = dados.filter((m) => m.resposta_de_id === raiz.id);
          const ultima = respostas.length ? respostas[respostas.length - 1] : raiz;
          seed[raiz.id] = ultima.criado_em;
        });
      salvarVisto(seed);
      return seed;
    });
  };

  useEffect(() => {
    if (temAcesso) recarregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temAcesso, selecionado]);

  // Stream ao vivo (SSE) — conectado a nível de app, então funciona em
  // qualquer página, não só dentro de /mensagens.
  useEffect(() => {
    if (!temAcesso) return;
    const candidatoId = user?.role === "admin" ? selecionado : undefined;
    const url = candidatoId ? `/api/mensagens/eventos?candidato_id=${candidatoId}` : "/api/mensagens/eventos";
    const origem = new EventSource(url);

    origem.onopen = () => setAoVivo(true);
    origem.onerror = () => setAoVivo(false);

    origem.addEventListener("nova", (e) => {
      const msg: Mensagem = JSON.parse((e as MessageEvent).data);
      setLista((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    });

    origem.addEventListener("removida", (e) => {
      const { id } = JSON.parse((e as MessageEvent).data);
      setLista((prev) => prev.filter((m) => m.id !== id));
    });

    return () => origem.close();
  }, [temAcesso]);

  const conversas: Conversa[] = useMemo(() => {
    const raizes = lista.filter((m) => !m.resposta_de_id);
    return raizes
      .map((raiz) => {
        const respostas = lista.filter((m) => m.resposta_de_id === raiz.id);
        const ultima = respostas.length ? respostas[respostas.length - 1] : raiz;
        return { raiz, respostas, ultima };
      })
      .sort((a, b) => new Date(b.ultima.criado_em).getTime() - new Date(a.ultima.criado_em).getTime());
  }, [lista]);

  const naoLida = (raizId: number, ultimaCriadoEm: string) => {
    const visto = vistoEm[raizId];
    return !visto || new Date(ultimaCriadoEm).getTime() > new Date(visto).getTime();
  };

  const marcarVista = (raizId: number, criadoEm: string) => {
    setVistoEm((prev) => {
      if (prev[raizId] === criadoEm) return prev;
      const atualizado = { ...prev, [raizId]: criadoEm };
      salvarVisto(atualizado);
      return atualizado;
    });
  };

  const contagemNaoLidas = useMemo(
    () => conversas.filter((c) => naoLida(c.raiz.id, c.ultima.criado_em)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversas, vistoEm]
  );

  return (
    <MensagensContext.Provider
      value={{ lista, conversas, aoVivo, naoLida, marcarVista, contagemNaoLidas, recarregar }}
    >
      {children}
    </MensagensContext.Provider>
  );
}

export function useMensagens() {
  const ctx = useContext(MensagensContext);
  if (!ctx) throw new Error("useMensagens deve ser usado dentro de MensagensProvider");
  return ctx;
}
